'use client';

import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { useBloodVisionIndia } from 'components/blood-vision/BloodVisionIndiaContext';
import IndiaPlanCard from './IndiaPlanCard';
import CategorySidebar from './CategorySidebar';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import InputPincode from './InputPincode';
import useScrollSpy from 'hooks/scroll/useScrollSpy';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import PlanComparisonTable from './PlanComparisonTable';
import { IndiaPlan } from './types';
import styles from './IndiaDynamicContent.module.css';

// Mobile Comparison Wrapper - CTA button that expands to show comparison table
interface MobileComparisonWrapperProps {
  selectedGender: 'male' | 'female' | null;
  visiblePlans: IndiaPlan[];
}

export const MobileComparisonCTA = ({
  className,
  ...props
}: React.ComponentProps<'button'>) => (
  <button
    className={`${styles.mobileComparisonCTA}${
      className ? ` ${className}` : ''
    }`}
    {...props}
  />
);

const ChevronUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M5 12.5L10 7.5L15 12.5"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileComparisonWrapper: React.FC<MobileComparisonWrapperProps> = ({
  selectedGender,
  visiblePlans,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCTAClick = () => {
    setIsExpanded(true);
    // Scroll to the comparison section when expanding
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 50);
  };

  const handleClose = () => {
    setIsExpanded(false);
  };

  return (
    <>
      <MobileComparisonCTA onClick={handleCTAClick}>
        Compare Plans
      </MobileComparisonCTA>

      <div
        className={styles.mobileComparisonContainer}
        ref={containerRef}
        data-expanded={isExpanded}
      >
        <div
          className={styles.mobileComparisonContent}
          data-expanded={isExpanded}
        >
          <button
            className={styles.mobileComparisonCloseButton}
            data-expanded={isExpanded}
            onClick={handleClose}
          >
            <ChevronUpIcon />
          </button>
          <PlanComparisonTable
            selectedGender={selectedGender}
            visiblePlans={visiblePlans}
          />
        </div>
      </div>
    </>
  );
};

// Recommended Plans Section - Vertical layout on mobile, grid on desktop
interface RecommendedPlansSectionProps {
  plans: IndiaPlan[];
  isMobile: boolean;
  isPlanInCart: (planId: number) => boolean;
  addPlanToCart: (plan: IndiaPlan) => void;
  removePlanFromCart: (planId: number) => void;
  hasUserProvidedPincode: boolean;
  pincodeServiceable: boolean;
  selectedGender: 'male' | 'female' | null;
  visiblePlans: IndiaPlan[];
  setVisiblePlans: (plans: IndiaPlan[]) => void;
  categorySectionRefs: React.MutableRefObject<
    Record<string, HTMLElement | null>
  >;
}

const RecommendedPlansSection: React.FC<RecommendedPlansSectionProps> = ({
  plans,
  isMobile,
  isPlanInCart,
  addPlanToCart,
  removePlanFromCart,
  hasUserProvidedPincode,
  pincodeServiceable,
  selectedGender,
  visiblePlans,
  setVisiblePlans,
  categorySectionRefs,
}) => {
  // Initialize visible plans with first 2 plans (or all if less than 2) for comparison table
  useEffect(() => {
    if (plans.length > 0) {
      const initialPlans = plans.slice(0, Math.min(2, plans.length));
      setVisiblePlans(initialPlans);
    }
  }, [plans, setVisiblePlans]);

  return (
    <section
      className={styles.categorySection}
      id="recommended"
      ref={(el) => {
        categorySectionRefs.current['recommended'] = el;
      }}
    >
      <h2 className={styles.categoryTitle}>
        {isMobile
          ? `Tests suggested for you (${plans.length})`
          : 'Blood Vision Plans'}
      </h2>

      {/* Plans grid - vertical on mobile, grid on desktop */}
      <div className={styles.plansGrid} data-plan-count={plans.length}>
        {plans.map((plan, planIndex) => (
          <IndiaPlanCard
            key={`recommended-${plan.id}-${planIndex}`}
            plan={plan}
            isInCart={isPlanInCart(plan.id)}
            onAdd={() => addPlanToCart(plan)}
            onRemove={() => removePlanFromCart(plan.id)}
            disabled={!hasUserProvidedPincode || !pincodeServiceable}
          />
        ))}
      </div>

      {/* Separator between plan cards and comparison table (desktop only) */}
      <div className={styles.plansComparisonSeparator} />

      {/* Show comparison table below Blood Vision Plans section */}
      {isMobile ? (
        <MobileComparisonWrapper
          selectedGender={selectedGender}
          visiblePlans={visiblePlans}
        />
      ) : (
        <PlanComparisonTable
          selectedGender={selectedGender}
          visiblePlans={undefined}
        />
      )}
    </section>
  );
};

// Memoized component for dynamic content that depends on context
const IndiaDynamicContent: React.FC = memo(() => {
  const {
    categories: apiCategories,
    availablePlans,
    error,
    hasUserProvidedPincode,
    pincodeInfo,
    addPlanToCart,
    removePlanFromCart,
    isPlanInCart,
  } = useBloodVisionIndia();

  const GlobalUI = useGlobalUI();
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarHeight, setSidebarHeight] = useState(100); // Default height

  // Simple state-based active category management
  const [activeCategory, setActiveCategory] = useState<string>('recommended');
  // Gender selection state - default to 'male' once API is fetched
  const [selectedGender, setSelectedGender] = useState<
    'male' | 'female' | null
  >(null);
  // Track visible plans in carousel for mobile comparison table
  const [visiblePlans, setVisiblePlans] = useState<IndiaPlan[]>([]);
  const isProgrammaticScrolling = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs for direct DOM access (eliminates lag from querySelector)
  const sidebarRef = useRef<HTMLElement | null>(null);
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const categorySectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // IMPORTANT: Use FIXED offsets for sticky positioning to prevent layout shift
  const hasMarketingBanner = isMarketingBannerActive();
  const marketingBannerHeight = hasMarketingBanner ? 32 : 0;

  // Desktop: Sidebar (with InputPincode inside) sticks directly below marketing banner
  const sidebarTopOffset = marketingBannerHeight;

  // Mobile positioning:
  // - CategorySidebar sticks below main header (InputPincode scrolls normally)
  // - Use global header visibility state and height (same pattern as SecondaryNav)
  // - GlobalUI.headerHeight already accounts for marketing banner + header combined
  const categoryMobileTopOffset = GlobalUI.headerVisible
    ? GlobalUI.headerHeight ?? 80
    : 0;

  // Measure sidebar height dynamically for mobile scroll offset
  useEffect(() => {
    if (!isMobile) return;

    const measureSidebar = () => {
      if (sidebarRef.current) {
        setSidebarHeight(sidebarRef.current.clientHeight);
      }
    };

    // Measure immediately and on resize
    measureSidebar();
    window.addEventListener('resize', measureSidebar);

    return () => {
      window.removeEventListener('resize', measureSidebar);
    };
  }, [isMobile, apiCategories.length]); // Re-measure when categories change

  // STANDARDIZED: Calculate consistent offset for both scroll and scrollSpy
  // This ensures scroll position and detection are perfectly aligned
  // Desktop: 100px from top of viewport (provides better visibility of category content)
  // Mobile: Account for sticky headers + sidebar height
  const scrollSpyOffset = useMemo(() => {
    if (isMobile) {
      // Add header offset + sidebar height + small padding for better visibility
      return categoryMobileTopOffset + sidebarHeight + 16;
    }
    return 100; // Desktop: 100px from top of viewport (reduced from 180px for better content visibility)
  }, [isMobile, categoryMobileTopOffset, sidebarHeight]);

  // Separate recommended and other categories
  const recommendedCategory = useMemo(
    () => apiCategories.find((cat) => cat.id === 'recommended'),
    [apiCategories],
  );
  const otherCategories = useMemo(
    () => apiCategories.filter((cat) => cat.id !== 'recommended'),
    [apiCategories],
  );

  // Use the existing useScrollSpy hook for automatic scroll detection
  // Include "recommended" and "other_tests" as scroll spy targets
  const categoryIds = useMemo(() => {
    const ids: string[] = [];
    if (recommendedCategory) ids.push('recommended');
    if (otherCategories.length > 0) ids.push('other_tests');
    return ids;
  }, [recommendedCategory, otherCategories]);
  const scrollSpyActiveCategory = useScrollSpy(categoryIds, scrollSpyOffset);

  // Update active category from scrollSpy ONLY when not programmatically scrolling
  useEffect(() => {
    if (!isProgrammaticScrolling.current && scrollSpyActiveCategory) {
      setActiveCategory(scrollSpyActiveCategory);
    }
  }, [scrollSpyActiveCategory]);

  // Set default gender to 'male' once API categories are loaded
  useEffect(() => {
    if (apiCategories && apiCategories.length > 0 && selectedGender === null) {
      setSelectedGender('male');
    }
  }, [apiCategories, selectedGender]);

  // Use API categories directly and filter by selected gender
  const plansByCategory = useMemo(() => {
    if (!apiCategories || apiCategories.length === 0) return {};
    if (selectedGender === null) return {}; // Don't show plans until gender is selected

    const grouped: Record<string, IndiaPlan[]> = {};
    apiCategories.forEach((category) => {
      // Filter plans based on selected gender's suitable_for property
      const filteredPlans =
        category.plans?.filter((plan) => {
          if (selectedGender === 'male') {
            return plan.suitable_for?.male === true;
          } else if (selectedGender === 'female') {
            return plan.suitable_for?.female === true;
          }
          return false;
        }) || [];
      grouped[category.id] = filteredPlans;
    });

    return grouped;
  }, [apiCategories, availablePlans, selectedGender]);

  // Track viewport size for mobile/desktop layout
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
    };

    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle mobile sidebar scrolling when active category changes
  useEffect(() => {
    if (!isMobile) return;

    const sidebarElement = sidebarRef.current;
    const activeCategoryElement = categoryRefs.current[activeCategory];

    if (sidebarElement && activeCategoryElement) {
      const sidebarWidth = sidebarElement.clientWidth;
      const categoryLeft = activeCategoryElement.offsetLeft;
      const categoryWidth = activeCategoryElement.clientWidth;

      const scrollLeft = categoryLeft - sidebarWidth / 2 + categoryWidth / 2;
      sidebarElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [activeCategory, isMobile]);

  // Scroll to category when clicked
  const scrollToCategory = (categoryId: string) => {
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Immediately set active category and block scrollSpy updates
    isProgrammaticScrolling.current = true;
    setActiveCategory(categoryId);

    const element = categorySectionRefs.current[categoryId];
    if (!element) {
      isProgrammaticScrolling.current = false;
      return;
    }

    // STANDARDIZED: Use getBoundingClientRect for accurate positioning
    // (offsetTop would be relative to positioned parent, not document)
    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    const targetPosition = elementTop - scrollSpyOffset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });

    // Re-enable scrollSpy after smooth scroll completes
    scrollTimeoutRef.current = setTimeout(() => {
      isProgrammaticScrolling.current = false;
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (error) {
    return <div className={styles.errorContainer}>{error}</div>;
  }

  // Show empty state if there are no categories
  // if (!apiCategories || apiCategories.length === 0) {
  //   return (
  //     <EmptyStateContainer>
  //       <EmptyStateTitle>No Plans Available</EmptyStateTitle>
  //       <EmptyStateText>
  //         We&apos;re currently updating our plans. Please check back soon.
  //       </EmptyStateText>
  //     </EmptyStateContainer>
  //   );
  // }

  return (
    <>
      {/* Mobile Header with Pincode Input - scrolls normally on mobile */}
      <header className={styles.mobileHeader}>
        <InputPincode />
      </header>

      {/* Main Content Layout */}
      <div className={styles.contentLayout}>
        {/* Sticky Sidebar - sticks below main header on mobile, contains InputPincode on desktop */}
        <CategorySidebar
          categories={apiCategories}
          activeCategory={activeCategory}
          onCategoryChange={scrollToCategory}
          topOffset={sidebarTopOffset}
          mobileTopOffset={categoryMobileTopOffset}
          sidebarRef={sidebarRef}
          categoryRefs={categoryRefs}
          selectedGender={selectedGender || 'male'}
          onGenderChange={setSelectedGender}
        />

        {/* Main Content that scrolls with the page */}
        <main className={styles.mainContent}>
          {/* Mobile Gender Selection */}
          <section className={styles.mobileGenderSection}>
            <h5 className={styles.mobileGenderTitle}>Select your Gender</h5>
            <div className={styles.mobileGenderOptions}>
              <button
                className={styles.mobileGenderOption}
                data-selected={selectedGender === 'male'}
                onClick={() => setSelectedGender('male')}
                type="button"
                aria-label="Select Male"
                aria-pressed={selectedGender === 'male'}
              >
                <div
                  className={styles.mobileRadioButton}
                  data-selected={selectedGender === 'male'}
                />
                <span
                  className={styles.mobileGenderOptionText}
                  data-selected={selectedGender === 'male'}
                >
                  Male
                </span>
              </button>
              <button
                className={styles.mobileGenderOption}
                data-selected={selectedGender === 'female'}
                onClick={() => setSelectedGender('female')}
                type="button"
                aria-label="Select Female"
                aria-pressed={selectedGender === 'female'}
              >
                <div
                  className={styles.mobileRadioButton}
                  data-selected={selectedGender === 'female'}
                />
                <span
                  className={styles.mobileGenderOptionText}
                  data-selected={selectedGender === 'female'}
                >
                  Female
                </span>
              </button>
            </div>
          </section>

          {/* Render recommended category first */}
          {recommendedCategory &&
            (() => {
              const plans = plansByCategory[recommendedCategory.id];
              if (!plans || plans.length === 0) return null;

              return (
                <RecommendedPlansSection
                  plans={plans}
                  isMobile={isMobile}
                  isPlanInCart={isPlanInCart}
                  addPlanToCart={addPlanToCart}
                  removePlanFromCart={removePlanFromCart}
                  hasUserProvidedPincode={hasUserProvidedPincode}
                  pincodeServiceable={
                    pincodeInfo?.pincode_serviceable !== false
                  }
                  selectedGender={selectedGender}
                  visiblePlans={visiblePlans}
                  setVisiblePlans={setVisiblePlans}
                  categorySectionRefs={categorySectionRefs}
                />
              );
            })()}

          {/* Render separator between recommended and other tests */}
          {recommendedCategory && otherCategories.length > 0 && (
            <div className={styles.categorySeparator} />
          )}

          {/* Render all other categories under "Other Tests" section */}
          {otherCategories.length > 0 &&
            (() => {
              // Get plan IDs from recommended category to exclude duplicates
              const recommendedPlanIds = new Set<number>();
              if (recommendedCategory) {
                const recommendedPlans =
                  plansByCategory[recommendedCategory.id];
                if (recommendedPlans && recommendedPlans.length > 0) {
                  recommendedPlans.forEach((plan) => {
                    recommendedPlanIds.add(plan.id);
                  });
                }
              }

              // Collect all plans from other categories, excluding plans already in recommended section
              const allOtherPlans: Array<{
                plan: IndiaPlan;
                categoryId: string;
              }> = [];
              otherCategories.forEach((category) => {
                const plans = plansByCategory[category.id];
                if (plans && plans.length > 0) {
                  plans.forEach((plan) => {
                    // Exclude plans that are already shown in recommended section (by plan ID)
                    if (!recommendedPlanIds.has(plan.id)) {
                      allOtherPlans.push({ plan, categoryId: category.id });
                    }
                  });
                }
              });

              if (allOtherPlans.length === 0) return null;

              return (
                <section
                  className={styles.categorySection}
                  id="other_tests"
                  ref={(el) => {
                    categorySectionRefs.current['other_tests'] = el;
                  }}
                >
                  <h2 className={styles.categoryTitle}>Other Tests</h2>
                  <div className={styles.otherTestsPlansGrid}>
                    {allOtherPlans.map(({ plan, categoryId }, planIndex) => (
                      <IndiaPlanCard
                        key={`${categoryId}-${plan.id}-${planIndex}`}
                        plan={plan}
                        isInCart={isPlanInCart(plan.id)}
                        onAdd={() => addPlanToCart(plan)}
                        onRemove={() => removePlanFromCart(plan.id)}
                        disabled={
                          !hasUserProvidedPincode ||
                          pincodeInfo?.pincode_serviceable === false
                        }
                      />
                    ))}
                  </div>
                </section>
              );
            })()}
        </main>
      </div>
    </>
  );
});

IndiaDynamicContent.displayName = 'IndiaDynamicContent';

export default IndiaDynamicContent;
